(function () {
    'use strict';
    /**
     * @callback SetPlayerSourceCallback
     * @returns {Promise}
     */
    /**
     * A utility method which is called by the async source plugin to set an asynchronous URL on the player. This will
     * also keep track of the player's state so that play can continue after loading is complete.
     * @param {Player} player - The instance of the player we are operating on.
     * @param {SetPlayerSourceCallback} srcFn - The callback function that will eventually generate the new source URL.
     * @param {object} drmSettings - values contained in config.json
     * @param {bool} isDrm - utilize drm playback
     * @param {string} authToken - contains myid token to pass via auth param in /las POST requests
     * @param {bool} first - is this the first time the video is being played?
     * @returns {Promise} A promise which is completed after the player has its source set.
     */
    function setPlayerSourceAsync(player, srcFn, drmSettings, isDrm, authToken, first) {
        var srcPromise = srcFn();
        return srcPromise.then(function (src) {
            // Save the state of the player prior to changing the source URL.
            var paused = player.paused();
            var autoplay = player.autoplay();
            var currentTime = player.currentTime();
            // In order to smooth the transition, we are pausing playback.
            if (!paused) {
                player.pause();
            }
            if (isDrm) {
                var videoSrc = [];
                src.forEach(function (manifestVideoEntry) {
                    var videojsVideoEntry = {
                        src: drmSettings.videoEndpoint + '/' + manifestVideoEntry['video'],
                        type: manifestVideoEntry['videoType'],
                    };
                    switch (manifestVideoEntry['videoType']) {
                        case 'application/dash+xml':
                            if (manifestVideoEntry['keySystems']) {
                                videojsVideoEntry['keySystemOptions'] = [];
                                manifestVideoEntry['keySystems'].forEach(function (keySystem) {
                                    videojsVideoEntry['keySystemOptions'].push({
                                        name: keySystem['keyType'],
                                        options: {
                                            serverURL: drmSettings.licenseServerUrl +
                                                '/la?url=' +
                                                encodeURIComponent(keySystem['licensePath'] +
                                                    '&CrmId=clipportal&AccountId=clipportal') +
                                                '&auth=' +
                                                authToken,
                                        },
                                    });
                                });
                            }
                            break;
                        case 'application/x-mpegURL':
                            if (manifestVideoEntry['keySystems']) {
                                manifestVideoEntry['keySystems'].forEach(function (keySystem) {
                                    if (keySystem['keyType'] === 'com.apple.fps.1_0') {
                                        videojsVideoEntry['protection'] = {
                                            keySystem: keySystem['keyType'],
                                            certificateUrl: drmSettings.licenseServerUrl +
                                                '/cert?accountId=clipportal&url=' +
                                                encodeURIComponent(keySystem['certPath']) +
                                                '&auth=' +
                                                authToken,
                                            licenseUrl: drmSettings.licenseServerUrl +
                                                '/la?url=' +
                                                encodeURIComponent(keySystem['licensePath'] +
                                                    '&CrmId=clipportal&AccountId=clipportal') +
                                                '&auth=' +
                                                authToken,
                                        };
                                    }
                                });
                            }
                            break;
                        default:
                            if (console)
                                console.warn('Video type ' +
                                    manifestVideoEntry['videoType'] +
                                    ' does not support DRM.');
                    }
                    videoSrc.push(videojsVideoEntry);
                });
                // DRM source
                player.src(videoSrc);
            }
            else {
                // Non DRM source
                player.src([
                    {
                        src: src,
                        type: 'video/mp4',
                    },
                ]);
            }
            if (!first) {
                // if 'autoplay' is set to false, need to force load after video reload
                // so that 'loadedmetadata' event will fire
                if (!autoplay)
                    player.load();
                // The player will once again be able to set time and play after the metadata from the new URL is loaded.
                player.one('loadedmetadata', function () {
                    player.currentTime(currentTime);
                    // Player autoplay setting will override user enacted paused state after video reload.
                    // So force pause or play depending upon stored 'paused' var.
                    paused ? player.pause() : player.play();
                });
            }
        });
    }
    /**
     * @typedef {Object} AsyncSourcePluginOptions
     * @property {SetPlayerSourceCallback} srcFn - The callback which will be called to fetch a new source URL.
     * @property {Boolean} reloadable - Whether this URL needs to be changed every so often.
     * @property {Number} reloadInterval - How long to wait before fetching a new source URL. Must be set if reloadable
     * is set to true. Should be divisible by 1000.
     */
    /**
     * This plugin will load a source asynchronously from a plugin, then set it.
     * @param {AsyncSourcePluginOptions} options - The options this component will be created with.
     * @this {Player} The video player instance.
     */
    function asyncSourcePlugin(options) {
        var player = this;
        // This is the banner that shows whenever the video is about to reload.
        var reloadWarning;
        player.on('ready', function () {
            reloadWarning = player.addChild('reloadWarning', {}, 0);
        });
        // Loads the source URL for the first time.
        setPlayerSourceAsync(player, options.srcFn, options.drmSettings, options.isDrm, options.authToken, true).then(function () {
            // reloadable should only be true if reloadInterval is set, but they are separate for convenience sake.
            if (options.reloadable) {
                // This is not exactly equal to the reload interval, but it'll be
                var counterStart = Math.floor(options.reloadInterval / 1000);
                // The counter will count down approximately every second until the video reloads.
                var counter = counterStart;
                // This will be disposed of when the video player itself is disposed.
                var warningIntervalId = setInterval(function () {
                    if (counter > 1) {
                        counter--;
                    }
                    else {
                        counter = counterStart;
                        setPlayerSourceAsync(player, options.srcFn);
                    }
                    // The banner may not have yet been created, so we need to check for it to avoid an error.
                    if (reloadWarning) {
                        reloadWarning.setTimeLeft(counter);
                    }
                }, 1000);
                // We need to remove the poster stuff from the player after play starts for the first time since
                // reloading will cause it to reappear.
                player.one('playing', function () {
                    player.removeChild('posterImage');
                    player.removeChild('bigPlayButton');
                    player.poster('');
                });
                // When the player is disposed of, we need to stop iterating the counter.
                player.on('dispose', function () {
                    clearInterval(warningIntervalId);
                });
            }
        });
    }
    // // Tells videojs about our custom "async source" plugin.
    // videojs.registerPlugin('asyncSource', asyncSourcePlugin);
})();
